import { useMemo } from 'react';

export const dayOpts = [
  { value: 'sunday', label: 'Sunday' },
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
];

export const monthOpts = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

export function useWorkOrderScheduleFields({
  workOrderSchedule,
  setWorkOrderSchedule,
  setScheduleErrors,
  includeActive = true,
  onActiveChange,
} = {}) {
  return useMemo(() => {
    const disabled = !workOrderSchedule.active;
    const fields = [];

    if (includeActive) {
      fields.push({
        field: 'active',
        label: 'Make Recurring',
        type: 'switch',
        size: 12,
        onChange: (nextValue) => {
          const checked = Boolean(nextValue);
          setWorkOrderSchedule((prev) => ({ ...prev, active: checked ? 1 : 0 }));
          if (onActiveChange) onActiveChange(checked);
        },
      });
    }

    fields.push({
      field: 'recurrencePattern',
      label: 'Recurring',
      type: 'select',
      options: [
        { value: 'daily', label: 'Daily' },
        { value: 'weekly', label: 'Weekly' },
        { value: 'monthly', label: 'Monthly' },
        { value: 'yearly', label: 'Yearly' },
      ],
      size: includeActive ? 3 : 4,
      disabled,
    });

    if (workOrderSchedule.recurrencePattern !== 'daily') {
      let everyText = '<div style="width: 100%; text-align: center">Every</div>';
      if (workOrderSchedule.recurrencePattern === 'monthly') {
        everyText = '<div style="width: 100%; text-align: center">Every</div>';
      }
      fields.push({ field: 'every-text', type: 'html', size: 1, html: everyText });
      fields.push({
        field: 'intervalValue',
        label: '-',
        type: 'number',
        size: 1,
        disabled,
        step: 1,
        min: 1,
        onChange: (e) => {
          const value = e.target.value;
          setWorkOrderSchedule((prev) => ({ ...prev, intervalValue: value }));
          const intVal = parseInt(value, 10);
          if (
            setScheduleErrors &&
            !isNaN(intVal) &&
            intVal > 0 &&
            intVal < 31
          ) {
            setScheduleErrors((prev) => {
              const { intervalValue, ...rest } = prev;
              return rest;
            });
          }
        },
      });
    }

    const daysOfWeekField = {
      field: 'daysOfWeek',
      label: 'Days of Week',
      type: 'multi-select',
      options: dayOpts,
      size: 5,
      disabled,
      onChange: (vals, setFormData, fieldName) => {
        setFormData(prev => ({ ...prev, [fieldName]: vals }));
        if (setScheduleErrors && Array.isArray(vals) && vals.length > 0) {
          setScheduleErrors(prev => {
            const { daysOfWeek, ...rest } = prev;
            return rest;
          });
        }
      },
    };

    if (workOrderSchedule.recurrencePattern === 'weekly') {
      fields.push({ field: 'days-text', type: 'html', size: 1, html: '<div style="width: 100%; text-align: center">Week(s) on:</div>' });
      fields.push(daysOfWeekField);
    } else if (workOrderSchedule.recurrencePattern === 'monthly') {
      fields.push({ field: 'days-text', type: 'html', size: 1, html: '<div style="width: 100%; text-align: center">Months</div>' });
      fields.push({ field: 'month-text', label: '', type: 'html', html: '<div style="width: 100%; text-align: center">On day</div>', size: 1 });
      fields.push({
        field: 'dayOfMonth',
        label: '-',
        type: 'number',
        size: 1,
        disabled,
        step: 1,
        min: 0,
        max: 31,
        onChange: (e) => {
          const value = e.target.value;
          setWorkOrderSchedule((prev) => ({ ...prev, dayOfMonth: value }));
          const intVal = parseInt(value, 10);
          if (
            setScheduleErrors &&
            !isNaN(intVal) &&
            intVal >= 0 &&
            intVal <= 31 &&
            Number(value) === intVal
          ) {
            setScheduleErrors((prev) => {
              const { dayOfMonth, ...rest } = prev;
              return rest;
            });
          }
        },
      });
      fields.push({ field: 'month-text2', label: '', type: 'html', html: '<div style="width: 100%; text-align: center">of the month</div>', size: 2 });
    } else if (workOrderSchedule.recurrencePattern === 'yearly') {
      fields.push({ field: 'days-text', type: 'html', size: 1, html: '<div style="width: 100%; text-align: center">Year(s) in</div>' });
      fields.push({
        field: 'monthOfYear',
        label: '-',
        type: 'select',
        options: monthOpts,
        size: 2,
        disabled,
        onChange: (e) => {
          const value = e.target.value;
          setWorkOrderSchedule((prev) => ({ ...prev, monthOfYear: value }));
          if (setScheduleErrors && value) {
            setScheduleErrors((prev) => {
              const { monthOfYear, ...rest } = prev;
              return rest;
            });
          }
        },
      });
      fields.push({ field: 'month-text', label: '', type: 'html', html: '<div style="width: 100%; text-align: center">on day</div>', size: 1 });
      fields.push({
        field: 'dayOfMonth',
        label: '-',
        type: 'number',
        size: 1,
        disabled,
        step: 1,
        min: 0,
        max: 31,
        onChange: (e) => {
          const value = e.target.value;
          setWorkOrderSchedule((prev) => ({ ...prev, dayOfMonth: value }));
          const intVal = parseInt(value, 10);
          if (
            setScheduleErrors &&
            !isNaN(intVal) &&
            intVal >= 0 &&
            intVal <= 31 &&
            Number(value) === intVal
          ) {
            setScheduleErrors((prev) => {
              const { dayOfMonth, ...rest } = prev;
              return rest;
            });
          }
        },
      });
      fields.push({ field: 'month-text2', label: '', type: 'html', html: '<div style="width: 100%; text-align: center">of the month</div>', size: 2 });
    } else if (workOrderSchedule.recurrencePattern !== 'daily') {
      fields.push({ field: 'days-text', type: 'html', size: 1, html: '<div style="width: 100%; text-align: center">Day(s) on:</div>' });
      fields.push(daysOfWeekField);
    }

    fields.push({ field: 'startDate', label: 'Starting On', type: 'date', size: 6, disabled });
    if (workOrderSchedule.recurrencePattern !== 'daily') {
      fields.push({ field: 'generalSpacer1', label: '', type: 'empty', size: 6, required: false });
    }
    fields.push({
      field: 'endDate',
      label: 'Ending By',
      type: 'date',
      size: 6,
      disabled: disabled || workOrderSchedule.noEndDate,
      onChange: (e) => {
        const value = e.target.value;
        setWorkOrderSchedule((prev) => ({
          ...prev,
          endDate: value,
          noEndDate: value ? 0 : 1,
        }));
      },
    });
    fields.push({ field: 'noEndDate', label: 'No End Date', type: 'switch', size: 6, disabled });

    return includeActive ? fields : fields.filter((f) => f.field !== 'active');
  }, [workOrderSchedule.recurrencePattern, workOrderSchedule.active, workOrderSchedule.noEndDate, includeActive, onActiveChange, setScheduleErrors, setWorkOrderSchedule]);
}

export default useWorkOrderScheduleFields;
